// Cortes y facturación por contrato: acumula los servicios servidos de un período,
// exige el ACTA DE CONFORMIDAD firmada por el cliente ANTES de facturar (el control
// que más destraba cobranza) y genera la factura de venta respetando la política de
// emisión del módulo Financiero. Catering → Facturación → Cortes y facturación.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText, FileSignature, Receipt, Ban, PlusCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import { fieldCls, num } from '@/features/inventario/shared';
import { useTabsStore } from '@/stores/tabs.store';
import { catRpc, invalidateCatering, money, SERVICIO_LABEL } from './shared';

interface Pendiente {
  contrato_id: string; contrato_codigo: string | null; contrato: string; cliente: string;
  corte: string; desde: string; hasta: string; dias: number; servicios: number;
  dias_sin_confirmar: number; monto: number;
}
interface CorteLinea { servicio: string; cantidad: number; precio: number; total: number; dias: number }
interface CorteResumen {
  id: string; codigo: string; estado: string; contrato_codigo: string | null; cliente: string;
  desde: string; hasta: string; total_servicios: number; subtotal: number; dias_sin_confirmar: number;
  acta_firmada_por: string | null; acta_fecha: string | null;
  factura: string | null; factura_estado: string | null;
}
interface CorteDetalle extends CorteResumen {
  contrato: string; contrato_id: string; identificacion: string;
  acta_cargo: string | null; acta_observaciones: string | null; acta_registrada_por: string | null;
  ticket_id: string | null; lineas: CorteLinea[]; modo?: 'bloque' | 'consumo';
}

const ESTADO_CLS: Record<string, string> = {
  borrador: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  acta_firmada: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300',
  facturado: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  anulado: 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300',
};
const ESTADO_LABEL: Record<string, string> = {
  borrador: 'Borrador', acta_firmada: 'Acta firmada', facturado: 'Facturado', anulado: 'Anulado',
};
const isoHoy = (): string => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function CortesCatering() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const openTab = useTabsStore((s) => s.openTab);
  const [selId, setSelId] = useState('');
  const [filtro, setFiltro] = useState('todos');

  const invalidar = () => invalidateCatering(qc);

  const { data: pendientes = [] } = useQuery<Pendiente[]>({
    queryKey: ['cat-cortes-pendientes'],
    queryFn: () => catRpc<Pendiente[]>('cat_cortes_pendientes'),
  });
  const { data: cortes = [] } = useQuery<CorteResumen[]>({
    queryKey: ['cat-cortes-listar', filtro],
    queryFn: () => catRpc<CorteResumen[]>('cat_cortes_listar', { _estado: filtro }),
  });
  const { data: detalle } = useQuery<CorteDetalle>({
    queryKey: ['cat-corte-obtener', selId], enabled: !!selId,
    queryFn: () => catRpc<CorteDetalle>('cat_corte_obtener', { _corte_id: selId }),
  });

  // --- Generar corte ---
  const [gen, setGen] = useState<{ contrato_id: string; desde: string; hasta: string } | null>(null);
  const generar = useMutation({
    mutationFn: (p: { contrato_id: string; desde: string; hasta: string }) =>
      catRpc<{ corte_id: string }>('cat_corte_generar', { _contrato_id: p.contrato_id, _desde: p.desde, _hasta: p.hasta }),
    onSuccess: (r) => { toast.success('Corte generado'); setGen(null); setSelId(r.corte_id); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // --- Firmar acta ---
  const [acta, setActa] = useState({ firmada_por: '', cargo: '', fecha: isoHoy(), obs: '' });
  const firmar = useMutation({
    mutationFn: () => catRpc('cat_corte_acta_firmar', {
      _corte_id: selId, _firmada_por: acta.firmada_por, _cargo: acta.cargo || null,
      _fecha: acta.fecha || null, _observaciones: acta.obs || null,
    }),
    onSuccess: () => { toast.success('Acta registrada'); setActa({ firmada_por: '', cargo: '', fecha: isoHoy(), obs: '' }); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // --- Facturar ---
  const facturar = useMutation({
    mutationFn: () => catRpc<{ codigo: string; emision: string; mensaje: string }>('cat_corte_facturar', { _corte_id: selId }),
    onSuccess: (r) => { toast.success(r.mensaje || `Corte ${r.codigo} facturado`); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // --- Anular ---
  const anular = useMutation({
    mutationFn: (motivo: string) => catRpc('cat_corte_anular', { _corte_id: selId, _motivo: motivo || null }),
    onSuccess: () => { toast.success('Corte anulado'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const totalPendiente = useMemo(() => pendientes.reduce((s, p) => s + Number(p.monto || 0), 0), [pendientes]);

  return (
    <div className="space-y-4 p-4">
      {dlg.ui}
      {/* Sin cortar: no dejar dinero sobre la mesa */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base"><Receipt className="h-4 w-4" /> Servicios sin cortar</CardTitle>
          <p className="pt-1 text-xs text-muted-foreground">
            Servicios ya prestados que todavía no están en ningún corte. Total por facturar: <strong>{money(totalPendiente, 2)}</strong>
          </p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          {pendientes.length ? (
            <table className="w-full text-sm">
              <thead><tr className="border-b text-left text-xs text-muted-foreground">
                <th className="py-1 pr-2">Contrato</th><th className="py-1 pr-2">Período con servicios</th>
                <th className="py-1 pr-2 text-right">Días</th><th className="py-1 pr-2 text-right">Servicios</th>
                <th className="py-1 pr-2 text-right">Monto</th><th className="py-1 pr-2">Sin confirmar</th><th />
              </tr></thead>
              <tbody>
                {pendientes.map((p) => (
                  <tr key={p.contrato_id} className="border-b last:border-0">
                    <td className="py-1.5 pr-2"><span className="font-medium">{p.contrato_codigo}</span> · {p.cliente}
                      <span className="ml-2 rounded-full bg-muted px-2 py-0.5 text-xs">corte {p.corte}</span></td>
                    <td className="py-1.5 pr-2 text-xs">{p.desde} → {p.hasta}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{p.dias}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{num(p.servicios, 0)}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{money(p.monto, 2)}</td>
                    <td className="py-1.5 pr-2">
                      {p.dias_sin_confirmar > 0
                        ? <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">{p.dias_sin_confirmar} día(s)</span>
                        : <span className="text-xs text-muted-foreground">—</span>}
                    </td>
                    <td className="py-1.5 text-right">
                      <Button size="sm" variant="outline" onClick={() => setGen({ contrato_id: p.contrato_id, desde: p.desde, hasta: p.hasta })}>
                        <PlusCircle className="mr-1 h-3.5 w-3.5" /> Generar corte
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <p className="text-sm text-muted-foreground">Todo lo servido ya está cortado.</p>}
        </CardContent>
      </Card>

      {/* Formulario de generación */}
      {gen && (
        <Card className="border-primary">
          <CardHeader className="pb-2"><CardTitle className="text-base">Generar corte</CardTitle></CardHeader>
          <CardContent className="flex flex-wrap items-end gap-3">
            <div><label className="block text-xs text-muted-foreground">Desde</label>
              <input type="date" className={`${fieldCls} w-40`} value={gen.desde} onChange={(e) => setGen({ ...gen, desde: e.target.value })} /></div>
            <div><label className="block text-xs text-muted-foreground">Hasta</label>
              <input type="date" className={`${fieldCls} w-40`} value={gen.hasta} onChange={(e) => setGen({ ...gen, hasta: e.target.value })} /></div>
            <Button disabled={generar.isPending} onClick={() => generar.mutate(gen)}>Generar</Button>
            <Button variant="ghost" onClick={() => setGen(null)}>Cancelar</Button>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
        {/* Lista de cortes */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Cortes</CardTitle>
            <div className="flex flex-wrap gap-1 pt-1">
              {['todos', 'borrador', 'acta_firmada', 'facturado', 'anulado'].map((e) => (
                <button key={e} onClick={() => setFiltro(e)}
                  className={`rounded-full px-2 py-0.5 text-xs ${filtro === e ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                  {e === 'todos' ? 'Todos' : ESTADO_LABEL[e]}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {cortes.map((k) => (
              <button key={k.id} onClick={() => setSelId(k.id)}
                className={`w-full rounded-md border px-3 py-2 text-left text-sm hover:bg-accent ${selId === k.id ? 'border-primary bg-accent' : ''}`}>
                <span className="flex items-center justify-between">
                  <span className="font-medium">{k.codigo}</span>
                  <span className={`rounded-full px-2 py-0.5 text-xs ${ESTADO_CLS[k.estado]}`}>{ESTADO_LABEL[k.estado] ?? k.estado}</span>
                </span>
                <span className="block text-xs text-muted-foreground">{k.contrato_codigo} · {k.cliente}</span>
                <span className="flex items-center justify-between pt-1 text-xs">
                  <span>{k.desde} → {k.hasta}</span>
                  <span className="font-mono">{money(k.subtotal, 2)}</span>
                </span>
                {k.factura && k.factura !== '(por emitir)' && (
                  <span className="block pt-0.5 text-[11px] text-green-700 dark:text-green-400">Factura {k.factura}</span>
                )}
              </button>
            ))}
            {!cortes.length && <p className="text-sm text-muted-foreground">Sin cortes en este estado.</p>}
          </CardContent>
        </Card>

        {/* Detalle del corte seleccionado */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2"><CardTitle className="text-base">Detalle del corte</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {!detalle ? <p className="text-sm text-muted-foreground">Selecciona un corte.</p> : (
              <>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <p className="font-medium">{detalle.codigo} — {detalle.cliente}</p>
                    <p className="text-xs text-muted-foreground">{detalle.contrato} · {detalle.desde} → {detalle.hasta}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {detalle.modo === 'consumo' && <span className="rounded-full bg-sky-100 px-2 py-0.5 text-xs text-sky-700 dark:bg-sky-900/30 dark:text-sky-300" title="Facturado por el consumo real de cada comensal">por consumo</span>}
                    <span className={`rounded-full px-2 py-0.5 text-xs ${ESTADO_CLS[detalle.estado]}`}>{ESTADO_LABEL[detalle.estado] ?? detalle.estado}</span>
                  </div>
                </div>

                {detalle.dias_sin_confirmar > 0 && (
                  <p className="rounded-md bg-amber-50 px-3 py-2 text-xs text-amber-800 dark:bg-amber-900/20 dark:text-amber-300">
                    Hay <strong>{detalle.dias_sin_confirmar}</strong> día(s) sin efectivos confirmados en este período: se facturaron con la cantidad planificada. Confírmalos en Contratos y pax si aún no lo hiciste.
                  </p>
                )}

                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="py-1 pr-2">Servicio</th><th className="py-1 pr-2 text-right">Cantidad</th>
                    <th className="py-1 pr-2 text-right">Precio</th><th className="py-1 pr-2 text-right">Días</th>
                    <th className="py-1 pr-2 text-right">Total</th>
                  </tr></thead>
                  <tbody>
                    {detalle.lineas.map((l) => (
                      <tr key={l.servicio} className="border-b last:border-0">
                        <td className="py-1.5 pr-2">{SERVICIO_LABEL[l.servicio] ?? l.servicio}</td>
                        <td className="py-1.5 pr-2 text-right font-mono">{num(l.cantidad, 0)}</td>
                        <td className="py-1.5 pr-2 text-right font-mono">{money(l.precio, 2)}</td>
                        <td className="py-1.5 pr-2 text-right font-mono">{l.dias}</td>
                        <td className="py-1.5 pr-2 text-right font-mono">{money(l.total, 2)}</td>
                      </tr>
                    ))}
                    <tr className="border-t-2 font-semibold">
                      <td className="py-1.5 pr-2" colSpan={4}>Subtotal ({num(detalle.total_servicios, 0)} servicios)</td>
                      <td className="py-1.5 pr-2 text-right font-mono">{money(detalle.subtotal, 2)}</td>
                    </tr>
                  </tbody>
                </table>

                {/* ACTA — el control clave */}
                <div className="rounded-md border p-3">
                  <p className="flex items-center gap-2 text-sm font-medium"><FileSignature className="h-4 w-4" /> Acta de conformidad</p>
                  {detalle.acta_firmada_por ? (
                    <p className="pt-1 text-xs text-muted-foreground">
                      Firmada por <strong>{detalle.acta_firmada_por}</strong>{detalle.acta_cargo ? ` (${detalle.acta_cargo})` : ''} el {detalle.acta_fecha}.
                      {detalle.acta_observaciones ? ` — ${detalle.acta_observaciones}` : ''}
                      {detalle.acta_registrada_por ? ` · registró: ${detalle.acta_registrada_por}` : ''}
                    </p>
                  ) : detalle.estado === 'borrador' ? (
                    <div className="grid gap-2 pt-2 sm:grid-cols-2">
                      <input className={fieldCls} placeholder="Firmada por (nombre del cliente)" value={acta.firmada_por}
                        onChange={(e) => setActa({ ...acta, firmada_por: e.target.value })} />
                      <input className={fieldCls} placeholder="Cargo" value={acta.cargo}
                        onChange={(e) => setActa({ ...acta, cargo: e.target.value })} />
                      <input type="date" className={fieldCls} value={acta.fecha}
                        onChange={(e) => setActa({ ...acta, fecha: e.target.value })} />
                      <input className={fieldCls} placeholder="Observaciones (opcional)" value={acta.obs}
                        onChange={(e) => setActa({ ...acta, obs: e.target.value })} />
                      <div className="sm:col-span-2">
                        <Button size="sm" disabled={!acta.firmada_por || firmar.isPending} onClick={() => firmar.mutate()}>
                          Registrar acta firmada
                        </Button>
                      </div>
                    </div>
                  ) : <p className="pt-1 text-xs text-muted-foreground">—</p>}
                </div>

                {/* Facturar */}
                <div className="flex flex-wrap items-center gap-2">
                  <Button disabled={detalle.estado !== 'acta_firmada' || facturar.isPending}
                    onClick={() => facturar.mutate()}>
                    <FileText className="mr-1 h-4 w-4" /> Facturar corte
                  </Button>
                  {detalle.estado === 'borrador' && (
                    <span className="text-xs text-muted-foreground">Registra primero el acta firmada para poder facturar.</span>
                  )}
                  {detalle.factura_estado && (
                    <span className="text-xs">
                      Factura: <strong>{detalle.factura}</strong>
                      <span className="ml-1 rounded-full bg-muted px-2 py-0.5">{detalle.factura_estado}</span>
                      <button className="ml-2 text-primary underline"
                        onClick={() => openTab({ id: '/cxc/facturacion', title: 'Facturación', path: '/cxc/facturacion' })}>
                        ver en Facturación
                      </button>
                    </span>
                  )}
                  {detalle.estado !== 'anulado' && detalle.estado !== 'facturado' && (
                    <Button size="sm" variant="ghost" className="text-red-600"
                      onClick={async () => {
                        const v = await dlg.pedir('Anular', [{ key: 'motivo', label: 'Motivo de anulación', minimo: 0 }]);
                        if (v) anular.mutate(v.motivo);
                      }}>
                      <Ban className="mr-1 h-3.5 w-3.5" /> Anular
                    </Button>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
